import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormHelperText,
  Grid,
  IconButton,
  Card,
  CardMedia
} from '@mui/material';
import { toast } from 'react-toastify';
import { addEditBlogs } from 'src/ApiServices';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import palette from 'src/theme/palette';
import useMediaQuery from '@mui/material/useMediaQuery';

// Rich Text Editor imports
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const BlogModal = ({ open, onClose, onSuccess, blog }) => {
  const [formData, setFormData] = useState({
    background_image: null,
    background_imagePreview: '',
    title: '',
    meta_title: '',
    slug: '',
    meta_description: '',
    description: ''
  });

  const [errors, setErrors] = useState({});
  const [warnings, setWarnings] = useState({});
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
  const [apiErrors, setApiErrors] = useState({});
  const isMobile = useMediaQuery('(max-width:600px)');

  // Rich Text Editor modules configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  };

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'script',
    'list',
    'bullet',
    'indent',
    'align',
    'blockquote',
    'code-block',
    'link',
    'image',
    'video'
  ];

  useEffect(() => {
    if (open) {
      if (blog) {
        // Edit mode - populate with existing data
        setIsEditMode(true);
        setIsSlugManuallyEdited(true); // In edit mode, slug is considered manually edited
        setFormData({
          background_image: null,
          background_imagePreview: blog.background_image || '',
          title: blog.title || '',
          meta_title: blog.meta_title || '',
          slug: blog.slug || '',
          meta_description: blog.meta_description || '',
          description: blog.description || ''
        });
      } else {
        // Add mode - reset form
        setIsEditMode(false);
        setIsSlugManuallyEdited(false); // Reset for add mode
        setFormData({
          background_image: null,
          background_imagePreview: '',
          title: '',
          meta_title: '',
          slug: '',
          meta_description: '',
          description: ''
        });
      }
      setErrors({});
      setWarnings({});
      setApiErrors({});
    }
  }, [open, blog]);

  // Improved slug generation function
  const generateSlug = (title) => {
    if (!title) return '';

    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Improved title change handler
  const handleTitleChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      title: value
    }));

    // Auto-generate slug ONLY if:
    // 1. We're in add mode (not edit mode)
    // 2. Slug hasn't been manually edited
    // 3. We're not currently editing the slug field
    if (!isEditMode && !isSlugManuallyEdited) {
      const generatedSlug = generateSlug(value);
      setFormData((prev) => ({
        ...prev,
        slug: generatedSlug
      }));
    }

    // Validate the title
    const error = validateField('title', value);
    setErrors((prev) => ({
      ...prev,
      title: error
    }));
  };

  // Improved slug change handler
  const handleSlugChange = (value) => {
    const previousSlug = formData.slug;

    setFormData((prev) => ({
      ...prev,
      slug: value
    }));

    // Check if user is manually editing the slug
    const generatedSlug = generateSlug(formData.title);

    // Mark as manually edited if:
    // 1. User changes the slug from the auto-generated one, OR
    // 2. User makes any change to the slug in edit mode
    if (isEditMode || value !== generatedSlug) {
      setIsSlugManuallyEdited(true);
    }

    // If user clears the slug field, reset the manual edit flag
    if (!value.trim()) {
      setIsSlugManuallyEdited(false);

      // Auto-generate slug if title exists
      if (formData.title && !isEditMode) {
        const newGeneratedSlug = generateSlug(formData.title);
        setFormData((prev) => ({
          ...prev,
          slug: newGeneratedSlug
        }));
      }
    }

    // Validate the slug
    const error = validateField('slug', value);
    setErrors((prev) => ({
      ...prev,
      slug: error
    }));
  };

  // Add this helper function to reset slug manual edit state
  const resetSlugManualEdit = () => {
    setIsSlugManuallyEdited(false);

    // Auto-generate slug from current title
    if (formData.title && !isEditMode) {
      const generatedSlug = generateSlug(formData.title);
      setFormData((prev) => ({
        ...prev,
        slug: generatedSlug
      }));
    }
  };

  // Function to validate meta title length and show warning
  const validateMetaTitle = (value) => {
    if (!value) return '';

    const length = value.length;
    if (length < 50) {
      return `Short (${length}/60): Consider expanding for better SEO`;
    } else if (length > 60) {
      return `Long (${length}/60): May be truncated in search results`;
    }
    return `Good (${length}/60)`;
  };

  // Function to validate meta description length and show warning
  const validateMetaDescription = (value) => {
    if (!value) return '';

    const length = value.length;
    if (length < 120) {
      return `Short (${length}/150): Consider expanding for better SEO`;
    } else if (length > 150) {
      return `Long (${length}/150): Enter between 120 to 150 for better SEO`;
    } else if (length >= 120 && length <= 150) {
      return `Ideal (${length}/150)`;
    }
    return `Good (${length}/150)`;
  };

  // Function to handle meta title change
  const handleMetaTitleChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      meta_title: value
    }));

    // Update warnings for meta title
    const warning = validateMetaTitle(value);
    setWarnings((prev) => ({
      ...prev,
      meta_title: warning
    }));
  };

  // Function to handle meta description change
  const handleMetaDescriptionChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      meta_description: value
    }));

    // Update warnings for meta description
    const warning = validateMetaDescription(value);
    setWarnings((prev) => ({
      ...prev,
      meta_description: warning
    }));
  };

  // Function to handle image clicks in the editor
  const handleEditorImageClick = (e) => {
    if (e.target.tagName === 'IMG' && window.Fancybox) {
      e.preventDefault();
      window.Fancybox.show([
        {
          src: e.target.src,
          type: 'image'
        }
      ]);
    }
  };

  // Initialize Fancybox for editor images when modal opens
  useEffect(() => {
    if (open && window.Fancybox) {
      // Small delay to ensure the editor content is rendered
      setTimeout(() => {
        window.Fancybox.bind('[data-fancybox="editor-gallery"]', {
          Thumbs: false,
          Toolbar: true
        });
      }, 300);
    }

    return () => {
      if (window.Fancybox) {
        window.Fancybox.destroy();
      }
    };
  }, [open, formData.description]);

  const validateField = (field, value) => {
    switch (field) {
      case 'title':
        return value ? '' : 'Title is required';
      case 'slug':
        return value ? '' : 'Slug is required';
      case 'description':
        // For rich text, we need to check if there's actual content (not just HTML tags)
        const strippedValue = value?.replace(/<[^>]*>/g, '').trim();
        return strippedValue ? '' : 'Description is required';
      default:
        return '';
    }
  };

  const handleInputChange = (field, value) => {
    // Use specialized handlers for specific fields
    switch (field) {
      case 'title':
        handleTitleChange(value);
        break;
      case 'slug':
        handleSlugChange(value);
        break;
      case 'meta_title':
        handleMetaTitleChange(value);
        break;
      case 'meta_description':
        handleMetaDescriptionChange(value);
        break;
      default:
        setFormData((prev) => ({
          ...prev,
          [field]: value
        }));

        // Validate the input
        const error = validateField(field, value);
        setErrors((prev) => ({
          ...prev,
          [field]: error
        }));
        break;
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData((prev) => ({
          ...prev,
          background_image: file,
          background_imagePreview: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    const requiredFields = ['title', 'slug', 'description'];
    const newErrors = {};
    setApiErrors({});

    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((error) => error !== '');
    if (hasErrors) {
      toast.error('Please fill all required fields.');
      return;
    }

    try {
      setSaving(true);
      const formattedSlug = formData.slug
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');

      // Prepare payload
      const payload = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key !== 'background_imagePreview' && formData[key] !== null) {
          if (key === 'background_image' && formData[key]) {
            payload.append('background_image', formData[key]);
          } else if (key !== 'background_image') {
            const value = key === 'slug' ? formattedSlug : formData[key];
            payload.append(key, value);
          }
        }
      });

      if (blog?.id) payload.append('id', blog.id);
      if (isEditMode && !formData.background_image && formData.background_imagePreview) {
        payload.append('keep_existing_image', 'true');
      }

      // Call API
      const response = await addEditBlogs(payload);
      console.log('response', response);
      if (response.status === 200 || response.status === 201) {
        const result = response?.data;
        toast.success(result.message || `Blog ${blog ? 'updated' : 'created'} successfully!`);
        onSuccess();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || errorData.message || 'Something went wrong.');
      }
    } catch (error) {
      console.error('Error saving blog:', error);

      if (error.response && error.response.status === 422) {
        const errorData = error.response.data;
        toast.error(errorData.error || errorData.message || 'Validation error occurred.');
      } else {
        toast.error(error.message || 'Unexpected error occurred. Please try again later.');
      }
    } finally {
      setSaving(false);
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({
      ...prev,
      background_image: null,
      background_imagePreview: ''
    }));
  };

  // Function to process HTML content for images
  const processHtmlContent = (html) => {
    if (!html) return html;

    // Add Fancybox attributes to images
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const images = tempDiv.getElementsByTagName('img');
    for (let img of images) {
      img.setAttribute('data-fancybox', 'editor-gallery');
      img.setAttribute('data-src', img.src);
      img.style.cursor = 'pointer';
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.borderRadius = '4px';
    }

    return tempDiv.innerHTML;
  };

  // Get color for warning based on character count
  const getWarningColor = (warning) => {
    if (!warning) return palette.text.secondary;
    if (warning.includes('Ideal')) return palette.success.main;
    if (warning.includes('Good')) return palette.success.main;
    if (warning.includes('Short')) return palette.warning.main;
    if (warning.includes('Long')) return palette.error.main;
    return palette.text.secondary;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={isMobile ? false : 'xl'}
      fullWidth
      sx={{
        '& .MuiDialog-container': {
          justifyContent: isMobile ? 'center' : 'flex-end',
          alignItems: 'center'
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '8px 0 0 8px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          margin: 0,
          height: isMobile ? '100vh' : '100vh',
          width: isMobile ? '100vw' : '90vw',
          maxWidth: isMobile ? '100vw' : '90vw'
        }
      }}
    >
      {/* Header with Close Button */}
      <DialogTitle
        sx={{
          backgroundColor: palette.primary.main,
          color: palette.primary.contrastText,
          py: 2,
          position: 'relative',
          borderBottom: `1px solid ${palette.primary.dark}`
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: 'poppinsSemiBold',
            color: palette.secondary.contrastText
          }}
        >
          {blog ? 'Edit Blog' : 'Add New Blog'}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            color: palette.secondary.contrastText
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 4,
          backgroundColor: palette.background.paper,
          height: 'calc(100vh - 140px)',
          overflow: 'auto'
        }}
      >
        <Grid container spacing={3}>
          {/* Background Image Field */}
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                fontFamily: 'poppinsSemiBold',
                color: palette.primary.titledark,
                mb: 2,
                mt: 2
              }}
            >
              Background Image{' '}
              {isEditMode && formData.background_imagePreview && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    color: palette.text.secondary,
                    fontFamily: 'poppinsRegular',
                    ml: 1
                  }}
                >
                  (Current image will be kept if no new image is selected)
                </Typography>
              )}
            </Typography>

            {!formData.background_imagePreview ? (
              <Card
                sx={{
                  border: `2px dashed ${palette.primary.inputborder}`,
                  borderRadius: 1,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: palette.background.neutral,
                  transition: 'all 0.3s ease',
                  height: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:hover': {
                    borderColor: palette.primary.main,
                    backgroundColor: palette.primary.lighter
                  }
                }}
                onClick={() => document.getElementById('background-image-upload').click()}
              >
                <input
                  id="background-image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <CloudUploadIcon
                  sx={{
                    fontSize: 32,
                    color: palette.primary.main,
                    mb: 1
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: palette.text.secondary,
                    fontFamily: 'poppinsMedium'
                  }}
                >
                  Click to upload background image
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: palette.text.disabled
                  }}
                >
                  PNG, JPG, JPEG up to 5MB
                </Typography>
              </Card>
            ) : (
              <Box sx={{ position: 'relative', maxWidth: '400px' }}>
                <Card sx={{ borderRadius: 2, border: `2px solid ${palette.primary.main}` }}>
                  <CardMedia
                    component="img"
                    height="150"
                    image={formData.background_imagePreview}
                    alt="Background Preview"
                    sx={{
                      objectFit: 'cover',
                      width: '100%'
                    }}
                  />
                </Card>
                <Button
                  variant="contained"
                  size="small"
                  onClick={removeImage}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: palette.error.main,
                    color: palette.error.contrastText,
                    minWidth: 'auto',
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    '&:hover': {
                      backgroundColor: palette.error.dark
                    }
                  }}
                >
                  Remove
                </Button>
              </Box>
            )}
          </Grid>

          {/* Title Field */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Title *"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              variant="outlined"
              error={!!errors.title}
              placeholder="Enter blog title"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  '& fieldset': {
                    borderColor: palette.primary.inputborder
                  },
                  '&:hover fieldset': {
                    borderColor: palette.primary.main
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: palette.primary.main,
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root': {
                  color: palette.text.secondary,
                  '&.Mui-focused': {
                    color: palette.primary.main
                  }
                }
              }}
            />
            {errors.title && (
              <FormHelperText
                error
                sx={{
                  fontFamily: 'poppinsRegular',
                  ml: 0
                }}
              >
                {errors.title}
              </FormHelperText>
            )}
          </Grid>

          {/* Meta Title Field */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Meta Title"
              value={formData.meta_title}
              onChange={(e) => handleInputChange('meta_title', e.target.value)}
              variant="outlined"
              placeholder="Enter meta title (50-60 characters recommended)"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  '& fieldset': {
                    borderColor: palette.primary.inputborder
                  },
                  '&:hover fieldset': {
                    borderColor: palette.primary.main
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: palette.primary.main,
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root': {
                  color: palette.text.secondary,
                  '&.Mui-focused': {
                    color: palette.primary.main
                  }
                }
              }}
            />
            {warnings.meta_title && (
              <FormHelperText
                sx={{
                  fontFamily: 'poppinsRegular',
                  ml: 0,
                  color: getWarningColor(warnings.meta_title),
                  fontSize: '0.75rem'
                }}
              >
                {warnings.meta_title}
              </FormHelperText>
            )}
          </Grid>

          {/* Slug Field */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Slug *"
              value={formData.slug}
              onChange={(e) => handleInputChange('slug', e.target.value)}
              variant="outlined"
              error={!!errors.slug}
              placeholder="Enter blog slug"
              helperText="Auto-generated from title, but can be edited"
              // toast.error(errorData.error || errorData.message || );

              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  '& fieldset': {
                    borderColor: palette.primary.inputborder
                  },
                  '&:hover fieldset': {
                    borderColor: palette.primary.main
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: palette.primary.main,
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root': {
                  color: palette.text.secondary,
                  '&.Mui-focused': {
                    color: palette.primary.main
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: 'poppinsRegular',
                  color: palette.text.secondary
                }
              }}
            />
            {errors.slug && (
              <FormHelperText
                error
                sx={{
                  fontFamily: 'poppinsRegular',
                  ml: 0
                }}
              >
                {errors.slug}
              </FormHelperText>
            )}
          </Grid>

          {/* Meta Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Meta Description"
              value={formData.meta_description}
              onChange={(e) => handleInputChange('meta_description', e.target.value)}
              variant="outlined"
              placeholder="Enter meta description (120-150 characters ideal)"
              multiline
              rows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  '& fieldset': {
                    borderColor:
                      formData.meta_description.length > 150
                        ? palette.error.main
                        : palette.primary.inputborder
                  },
                  '&:hover fieldset': {
                    borderColor:
                      formData.meta_description.length > 150
                        ? palette.error.main
                        : palette.primary.main
                  },
                  '&.Mui-focused fieldset': {
                    borderColor:
                      formData.meta_description.length > 150
                        ? palette.error.main
                        : palette.primary.main,
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root': {
                  color: palette.text.secondary,
                  '&.Mui-focused': {
                    color:
                      formData.meta_description.length > 150
                        ? palette.error.main
                        : palette.primary.main
                  }
                }
              }}
            />
            {warnings.meta_description && (
              <FormHelperText
                sx={{
                  fontFamily: 'poppinsRegular',
                  ml: 0,
                  color: getWarningColor(warnings.meta_description),
                  fontSize: '0.75rem'
                }}
              >
                {warnings.meta_description}
              </FormHelperText>
            )}
          </Grid>

          {/* Rich Text Editor for Description */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 0.5, sm: 0 },
                mb: 1
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: 'poppinsSemiBold',
                  color: palette.primary.titledark
                }}
              >
                Description *
              </Typography>

              {/* Quick Guide for User */}
              <Typography
                variant="caption"
                sx={{
                  color: palette.text.secondary,
                  fontFamily: 'poppinsRegular'
                }}
              >
                Use toolbar for formatting. Images in editor are clickable for preview.
              </Typography>
            </Box>

            <Box
              sx={{
                border: errors.description
                  ? `1px solid ${palette.error.main}`
                  : `1px solid ${palette.primary.inputborder}`,
                borderRadius: 1,
                overflow: 'hidden',
                '& .ql-toolbar': {
                  border: 'none',
                  borderBottom: `1px solid ${palette.primary.inputborder}`,
                  backgroundColor: palette.background.neutral,
                  '& .ql-picker-label': {
                    fontFamily: 'poppinsRegular'
                  },
                  '& .ql-picker-options': {
                    fontFamily: 'poppinsRegular'
                  }
                },
                '& .ql-container': {
                  border: 'none',
                  fontFamily: 'poppinsRegular',
                  fontSize: '14px',
                  minHeight: '350px'
                },
                '& .ql-editor': {
                  minHeight: '350px',
                  fontFamily: 'poppinsRegular',
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    fontFamily: 'poppinsSemiBold',
                    marginTop: '8px',
                    marginBottom: '8px'
                  },
                  '& a': {
                    color: palette.primary.main,
                    textDecoration: 'underline',
                    '&:hover': {
                      color: palette.primary.dark
                    }
                  },
                  '& img': {
                    cursor: 'pointer',
                    borderRadius: '4px',
                    transition: 'all 0.3s ease',
                    maxWidth: '100%',
                    height: 'auto',
                    margin: '8px 0',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: `0 4px 12px ${palette.primary.main}40`
                    }
                  },
                  '&.ql-blank::before': {
                    color: palette.text.disabled,
                    fontStyle: 'normal',
                    fontFamily: 'poppinsRegular'
                  }
                },
                '&:hover': {
                  borderColor: palette.primary.main
                },
                '&:focus-within': {
                  borderColor: palette.primary.main,
                  borderWidth: 2
                }
              }}
              onClick={handleEditorImageClick}
            >
              <ReactQuill
                value={formData.description}
                onChange={(value) => handleInputChange('description', value)}
                modules={modules}
                formats={formats}
                placeholder="Enter blog description... You can use headings (H1-H6) from dropdown and add links/images using toolbar buttons."
                theme="snow"
              />
            </Box>

            {errors.description && (
              <FormHelperText
                error
                sx={{
                  fontFamily: 'poppinsRegular',
                  ml: 0,
                  mt: 1
                }}
              >
                {errors.description}
              </FormHelperText>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'end',
          gap: 2,
          p: 3,
          backgroundColor: palette.background.neutral,
          borderTop: `1px solid ${palette.divider}`
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            minWidth: 120,
            borderRadius: 1,
            borderColor: palette.primary.inputborder,
            color: palette.primary.titledark,
            fontFamily: 'poppinsMedium',
            textTransform: 'none',
            py: 1,
            '&:hover': {
              borderColor: palette.primary.main,
              backgroundColor: palette.primary.lighter
            }
          }}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            minWidth: 120,
            borderRadius: 1,
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            fontFamily: 'poppinsMedium',
            textTransform: 'none',
            py: 1,
            '&:hover': {
              backgroundColor: palette.primary.dark
            },
            '&:disabled': {
              backgroundColor: palette.primary.disabled
            }
          }}
          disabled={saving}
        >
          {saving ? 'Saving...' : blog ? 'Update Blog' : 'Save Blog'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlogModal;
